import { LowStockMeta, LimitedMeta, FundingMeta, LowStockVariantId, ProductMetaCode } from '../value-objects';
import { ProductId } from '@services/MarppleShop/features/ProductMeta';

export interface MetaCodeVariantProps {
  id?: number;
  code: ProductMetaCode;
  variant: string;
  createdAt: Date;
}

export class MetaCodeVariant {
  private readonly _id?: number;

  private readonly _code: ProductMetaCode;
  private readonly _variant: string;

  constructor(props: MetaCodeVariantProps) {
    this._id = props.id;
    this._code = props.code;
    this._variant = props.variant;
  }

  static create(props: Omit<MetaCodeVariantProps, 'id'>): MetaCodeVariant {
    return new MetaCodeVariant(props);
  }

  static from(props: MetaCodeVariantProps): MetaCodeVariant {
    return new MetaCodeVariant(props);
  }

  get id(): number | undefined {
    return this._id;
  }

  getIdOrThrow(): number {
    if (!this._id) {
      throw new Error('초기화 되지 않은 MetaCodeVariant입니다.');
    }

    return this._id;
  }

  get code(): ProductMetaCode {
    return this._code;
  }

  get variant(): string {
    return this._variant;
  }
}

export interface ProductMetaProps {
  productId: ProductId;

  low_stock_threshold_meta?: LowStockMeta;
  limited_meta?: LimitedMeta;
  funding_meta?: FundingMeta;
  created_at?: Date;
  updated_at?: Date;

  /**
   * @deprecated 인터페이스 고민 중
   */
  variants?: MetaCodeVariant[];
}

/**
 * 상품의 메타데이터를 저장하는 엔티티
 */
export class ProductMeta {
  private readonly productId: ProductId;

  // 품절 임박
  private lowStockMeta: LowStockMeta | null;

  // 한정판
  private limitedMeta: LimitedMeta | null;

  // 펀딩
  private fundingMeta: FundingMeta | null;

  private readonly created_at: Date;
  private updated_at: Date;

  private constructor(props: ProductMetaProps) {
    this.productId = props.productId;
    this.lowStockMeta = props.low_stock_threshold_meta ?? null;
    this.limitedMeta = props.limited_meta ?? null;
    this.fundingMeta = props.funding_meta ?? null;
    this.created_at = props.created_at || new Date();
    this.updated_at = props.updated_at || new Date();
  }

  // Getters
  getProductId(): ProductId {
    return this.productId;
  }

  getLowStockThreshold(): LowStockMeta | null {
    return this.lowStockMeta;
  }

  getLimitedMeta(): LimitedMeta | null {
    return this.limitedMeta;
  }

  getFundingMeta(): FundingMeta | null {
    return this.fundingMeta;
  }

  getCreatedAt(): Date {
    return this.created_at;
  }

  getUpdatedAt(): Date {
    return this.updated_at;
  }

  // Low Stock Threshold methods
  setLowStockMeta(variant: LowStockVariantId): void {
    this.lowStockMeta = new LowStockMeta(variant);
    this.updateTimestamp();
  }

  removeLowStockMeta(): void {
    this.lowStockMeta = null;
    this.updateTimestamp();
  }

  shouldShowLowStock(current_stock: number): boolean {
    if (!this.lowStockMeta) {
      return false;
    }
    return this.lowStockMeta.shouldShowLowStock(current_stock);
  }

  // Limited methods
  setAsLimited(): void {
    this.limitedMeta = new LimitedMeta(true);
    this.updateTimestamp();
  }

  setAsUnlimited(): void {
    this.limitedMeta = new LimitedMeta(false);
    this.updateTimestamp();
  }

  removeLimitedMeta(): void {
    this.limitedMeta = null;
    this.updateTimestamp();
  }

  // Funding methods
  setFundingTarget(target_amount: number): void {
    if (!this.fundingMeta) {
      this.fundingMeta = FundingMeta.create({ target_amount, current_amount: 0 });
    } else {
      this.fundingMeta = this.fundingMeta.setTarget(target_amount);
    }
    this.updateTimestamp();
  }

  addFunding(amount: number): void {
    if (!this.fundingMeta) {
      throw new Error('Funding meta not set. Set target amount first.');
    }
    this.fundingMeta = this.fundingMeta.addFunding(amount);
    this.updateTimestamp();
  }

  subtractFunding(amount: number): void {
    if (!this.fundingMeta) {
      throw new Error('Funding meta not set. Set target amount first.');
    }
    this.fundingMeta = this.fundingMeta.subtractFunding(amount);
    this.updateTimestamp();
  }

  removeFundingMeta(): void {
    this.fundingMeta = null;
    this.updateTimestamp();
  }

  private updateTimestamp(): void {
    this.updated_at = new Date();
  }

  /**
   * ProductMeta 생성 (DB 저장 전)
   */
  static create(props: ProductMetaProps): ProductMeta {
    return new ProductMeta(props);
  }

  /**
   * DB에서 조회한 ProductMeta 데이터를 통해 인스턴스를 생성할 때 사용
   */
  static from(props: ProductMetaProps): ProductMeta {
    return new ProductMeta(props);
  }
}
